import type {
	EventSensor,
	InstallationDto,
	Location,
	Zone,
} from "@sitescene/types";

import type { MarkerFlashState } from "@/components/map/marker-flash-state";
import type { WebSocketEventDetail } from "@/utils/websocket-event-converter";

import { getRefId } from "@/features/locations/utils/installation-ids";
import { isMetadataStream } from "@/utils/sensor-nesting";

import type { IndexedMapSensor } from "./build-map-features";
import type { MapDrillState } from "./map-drill-types";

export type { MarkerFlashState } from "@/components/map/marker-flash-state";

/**
 * Live-alert highlighting for whatever is currently on the dashboard map.
 *
 * Only alerts that arrive while the app is open count (the live connection is
 * app-wide). Alerts already in the list when the page loaded do not light up
 * clusters or pins. Dashboard location/zone/sensor filters also scope which
 * pills appear.
 *
 * While a live alert is "new", the matching cluster/pin pulses. After that,
 * clusters keep an "N Alerts" count for the rest of the session, and sensor
 * pins keep the color of the most recent live alert that hit them.
 */
export type MapEventFlashTarget = {
	eventId: string;
	installationId?: string;
	/** Still in the "new alert" pulse. Afterward the event still counts on clusters. */
	isPulsing?: boolean;
	locationId?: string;
	sensorIds: string[];
	severity: number;
	zoneIds: string[];
};

type EnrichedWebSocketEventDetail = WebSocketEventDetail & {
	installation?: InstallationDto | string;
	sensors?: EventSensor[];
	zones?: Array<string | Zone>;
};

const toIdList = (values: Array<null | string | undefined>): string[] => [
	...new Set(values.filter((value): value is string => Boolean(value))),
];

const featureId = (feature: GeoJSON.Feature): string | undefined =>
	feature.id == null ? undefined : String(feature.id);

const featureKind = (feature: GeoJSON.Feature): string | undefined =>
	typeof feature.properties?.kind === "string"
		? feature.properties.kind
		: undefined;

const featureStringProp = (
	feature: GeoJSON.Feature,
	key: string,
): string | undefined => {
	const value = feature.properties?.[key];
	return typeof value === "string" ? value : undefined;
};

/**
 * Object-analytics streams are filtered off the map (they share a camera's
 * place). Live alerts often attribute to those stream ids, so zone pills light
 * up while sensor pins stay plain. Map each metadata stream to the camera it
 * narrates so drill-down pins can match.
 */
export const buildMapFlashSensorAliases = (
	locations: Location[],
): Map<string, string> => {
	const aliases = new Map<string, string>();

	for (const location of locations) {
		for (const zone of location.zones ?? []) {
			for (const sensor of zone.sensors ?? []) {
				if (!isMetadataStream(sensor)) continue;
				const parentId = getRefId(sensor.videoSensor);
				if (parentId) {
					aliases.set(sensor.id, parentId);
				}
			}
		}
	}

	return aliases;
};

const remapSensorIds = (
	sensorIds: string[],
	sensorIdAliases?: Map<string, string>,
): string[] => {
	if (!sensorIdAliases || sensorIdAliases.size === 0) {
		return toIdList(sensorIds);
	}
	return toIdList(sensorIds.map((id) => sensorIdAliases.get(id) ?? id));
};

const targetFromEventFields = ({
	eventId,
	installation,
	installationId,
	sensors,
	severity,
	zones,
}: {
	eventId: string;
	installation?: InstallationDto | string;
	installationId?: string;
	sensors?: EventSensor[];
	severity: number;
	zones?: Array<string | Zone>;
}): MapEventFlashTarget => {
	const locationId =
		installation && typeof installation !== "string"
			? getRefId(installation.location)
			: undefined;

	return {
		eventId,
		installationId:
			installationId ??
			(typeof installation === "string" ? installation : installation?.id),
		locationId,
		sensorIds: toIdList((sensors ?? []).map((sensor) => sensor.id)),
		severity,
		zoneIds: toIdList(
			(zones ?? []).map((zone) => (typeof zone === "string" ? zone : zone.id)),
		),
	};
};

/**
 * Pull location / zone / sensor ids off a live alert so the map knows what to highlight.
 */
export const extractMapEventFlashTarget = (
	eventDetail: WebSocketEventDetail,
): MapEventFlashTarget | undefined => {
	if (!eventDetail.id) return undefined;

	const detail = eventDetail as EnrichedWebSocketEventDetail;
	return targetFromEventFields({
		eventId: eventDetail.id,
		installation: detail.installation,
		installationId: eventDetail.installationId,
		sensors: detail.sensors,
		severity: eventDetail.severity,
		zones: detail.zones,
	});
};

/**
 * If the live payload is missing a location or zone, fill them in from the
 * org's mapped sensors (a sensor id or installation is enough).
 */
export const enrichMapEventFlashTarget = (
	target: MapEventFlashTarget,
	indexedSensors: IndexedMapSensor[],
	sensorIdAliases?: Map<string, string>,
): MapEventFlashTarget => {
	let locationId = target.locationId;
	const zoneIds = new Set(target.zoneIds);
	const sensorIds = new Set(remapSensorIds(target.sensorIds, sensorIdAliases));
	const indexedById = new Map(
		indexedSensors.map((sensor) => [sensor.id, sensor]),
	);

	for (const sensorId of [...sensorIds]) {
		const sensor = indexedById.get(sensorId);
		if (!sensor) continue;
		locationId ??= sensor.locationId;
		zoneIds.add(sensor.zoneId);
	}

	for (const sensor of indexedSensors) {
		const matchesZone = zoneIds.has(sensor.zoneId);
		const matchesInstallation =
			Boolean(target.installationId) &&
			getRefId(sensor.installation) === target.installationId;

		if (matchesZone || matchesInstallation) {
			locationId ??= sensor.locationId;
		}
	}

	const hitsVisiblePin = [...sensorIds].some((id) => indexedById.has(id));
	if (!hitsVisiblePin && zoneIds.size > 0) {
		for (const sensor of indexedSensors) {
			if (!zoneIds.has(sensor.zoneId)) continue;
			sensorIds.add(sensor.id);
			locationId ??= sensor.locationId;
		}
	}

	return {
		...target,
		locationId,
		sensorIds: [...sensorIds],
		zoneIds: [...zoneIds],
	};
};

/**
 * Only highlight what the operator can currently see: location cards at the
 * org view, zone cards inside a location, or sensor pins inside a location
 * (including dimmed sibling-zone pins at the sensors drill).
 */
const matchVisibleFlashFeatures = (
	drill: MapDrillState,
	target: MapEventFlashTarget,
	features: GeoJSON.Feature[],
): GeoJSON.Feature[] => {
	if (drill.level === "locations") {
		if (!target.locationId) return [];
		return features.filter(
			(feature) =>
				featureKind(feature) === "locationCluster" &&
				featureStringProp(feature, "locationId") === target.locationId,
		);
	}

	if (drill.level === "zones") {
		if (target.locationId && drill.locationId !== target.locationId) {
			return [];
		}
		if (target.zoneIds.length === 0) return [];
		const zoneIds = new Set(target.zoneIds);
		return features.filter(
			(feature) =>
				featureKind(feature) === "zoneCluster" &&
				zoneIds.has(featureStringProp(feature, "zoneId") ?? ""),
		);
	}

	if (target.locationId && drill.locationId !== target.locationId) {
		return [];
	}
	// Sibling-zone pins stay on screen (dimmed) at the sensors drill — keep
	// their live-alert color even when the focused zone is not the alert's.
	if (target.sensorIds.length === 0) return [];

	const sensorIds = new Set(target.sensorIds);
	return features.filter((feature) => {
		if (featureKind(feature) !== "sensor") return false;
		const id = featureId(feature) ?? featureStringProp(feature, "id");
		return Boolean(id && sensorIds.has(id));
	});
};

type MarkerAlertAccumulator = {
	eventIds: Set<string>;
	isFlashing: boolean;
	latestSeverity: number;
	severity: number;
};

const applyTargetToFeatures = ({
	accumulator,
	drill,
	features,
	indexedSensors,
	isFlashing,
	sensorIdAliases,
	target,
}: {
	accumulator: Record<string, MarkerAlertAccumulator>;
	drill: MapDrillState;
	features: GeoJSON.Feature[];
	indexedSensors: IndexedMapSensor[];
	isFlashing: boolean;
	sensorIdAliases?: Map<string, string>;
	target: MapEventFlashTarget;
}) => {
	const enriched = enrichMapEventFlashTarget(
		target,
		indexedSensors,
		sensorIdAliases,
	);
	for (const feature of matchVisibleFlashFeatures(drill, enriched, features)) {
		const id = featureId(feature);
		if (!id) continue;
		const existing = accumulator[id];
		if (!existing) {
			accumulator[id] = {
				eventIds: new Set([target.eventId]),
				isFlashing,
				latestSeverity: enriched.severity,
				severity: enriched.severity,
			};
			continue;
		}
		existing.eventIds.add(target.eventId);
		existing.isFlashing = existing.isFlashing || isFlashing;
		// Last matching live alert wins for pin color; clusters still keep the highest.
		existing.latestSeverity = enriched.severity;
		if (enriched.severity > existing.severity) {
			existing.severity = enriched.severity;
		}
	}
};

/**
 * Match live alerts to the clusters and pins currently on screen.
 */
export const resolveVisibleMarkerFlashes = ({
	drill,
	features,
	flashes,
	indexedSensors,
	sensorIdAliases,
}: {
	drill: MapDrillState;
	features: GeoJSON.Feature[];
	flashes: MapEventFlashTarget[];
	indexedSensors: IndexedMapSensor[];
	sensorIdAliases?: Map<string, string>;
}): Record<string, MarkerFlashState> => {
	const accumulator: Record<string, MarkerAlertAccumulator> = {};

	for (const flash of flashes ?? []) {
		applyTargetToFeatures({
			accumulator,
			drill,
			features,
			indexedSensors,
			isFlashing: flash.isPulsing !== false,
			sensorIdAliases,
			target: flash,
		});
	}

	const result: Record<string, MarkerFlashState> = {};
	for (const [id, value] of Object.entries(accumulator)) {
		result[id] = {
			alertCount: value.eventIds.size,
			isFlashing: value.isFlashing,
			latestSeverity: value.latestSeverity,
			severity: value.severity,
		};
	}
	return result;
};
